import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import capitalize from 'lodash/capitalize'

import { Box, Typography, Stack, Divider } from '@mui/material'
import Grid from '@mui/material/Grid'

import { getPaymentMethod, getPaymentMethodImage } from 'utils/payment'

import { convertDateTimeToPreferedTimezone } from 'data/dateTime/convertDateTimeToPreferedTimezone'

import { PAYMENT_METHODS, PAYMENT_METHOD_IMAGES, DEBIT, CREDIT } from 'settings/constants/payment'
import HHDisplayMoney from 'components/common/HHDisplayMoney'
import PaymentStatusChip from 'components/common/chip/PaymentStatusChip'
import { green, red } from '@mui/material/colors'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { isPaymentsV15FeatureAvailable } from 'data/launch-darkly/featureSelectors'
import { TOTAL_COLUMN_WIDTH, AMOUNT_APPLIED_COLUMN_WIDTH } from './settings'

const { ACH, CREDIT_CARD } = PAYMENT_METHODS

const PaymentActivity = ({ chipGridWidth = 120, payment, showAppliedColumn = false, align = 'center', marginY = 0 }) => {
  const {
    transactionStatus,
    paymentMethod,
    transactionAmountCents,
    transactionPostedDate,
    creditCardType,
    accountLastFour,
    paymentAppliedAmountCents,
    appliedAmountCents,
    batchId,
    debitCredit,
  } = payment || {}
  const flags = useFlags()
  const isPaymentsV15Enabled = isPaymentsV15FeatureAvailable({ flags })
  const paymentMethodName = paymentMethod === ACH ? paymentMethod : capitalize(getPaymentMethod(paymentMethod))
  const { fullDateTime } = convertDateTimeToPreferedTimezone({ dateTime: transactionPostedDate })
  const paymentMethodImage = paymentMethod === CREDIT_CARD ? getPaymentMethodImage(creditCardType) : PAYMENT_METHOD_IMAGES[paymentMethod]
  const isDebit = debitCredit === DEBIT
  const isCredit = debitCredit === CREDIT

  const debitCreditSx = useMemo(() => {
    if (isPaymentsV15Enabled) {
      return { color: appliedAmountCents >= 0 ? green[500] : red[500] }
    }
    return {
      ...(isDebit && { color: green[500] }),
      ...(isCredit && { color: red[500] }),
    }
  }, [isCredit, isDebit, isPaymentsV15Enabled, appliedAmountCents])

  return (
    <>
      <Grid container spacing={2.5} wrap="nowrap" alignItems={align} size={12}>
        <Grid minWidth={chipGridWidth} my={marginY} size="auto">
          <PaymentStatusChip status={transactionStatus} />
        </Grid>
        <Grid my={marginY} size="grow">
          <Box display="flex" alignItems="center">
            <img src={paymentMethodImage} alt="" />
            <Stack ml={1}>
              <Typography display="inline" variant="h6" fontWeight={500}>
                {creditCardType && accountLastFour ? `${creditCardType} •••• ${accountLastFour}` : paymentMethodName}
              </Typography>
              <Typography variant="caption">{batchId}</Typography>
            </Stack>
          </Box>
        </Grid>
        <Grid width={TOTAL_COLUMN_WIDTH} my={marginY}>
          <Typography display="flex" alignItems="center" justifyContent="flex-end" variant="h6" fontWeight={500}>
            <HHDisplayMoney value={transactionAmountCents} formatToDollars />
          </Typography>
          <Typography display="flex" alignItems="center" justifyContent="flex-end" variant="caption">
            {fullDateTime}
          </Typography>
        </Grid>
        {showAppliedColumn && (
          <>
            <Grid display="flex" size="auto">
              <Divider flexItem orientation="vertical" />
            </Grid>
            <Grid width={AMOUNT_APPLIED_COLUMN_WIDTH} my={marginY}>
              <Typography display="flex" alignItems="center" justifyContent="flex-end" variant="h6" fontWeight={500} sx={debitCreditSx}>
                {isDebit && '+'}
                {isCredit && '-'}
                <HHDisplayMoney value={isPaymentsV15Enabled ? appliedAmountCents : paymentAppliedAmountCents} formatToDollars />
              </Typography>
            </Grid>
          </>
        )}
      </Grid>
    </>
  )
}

PaymentActivity.propTypes = {
  chipGridWidth: PropTypes.number,
  payment: PropTypes.object.isRequired,
  showAppliedColumn: PropTypes.string,
  align: PropTypes.string,
  marginY: PropTypes.string,
}

export default PaymentActivity
